import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAccounts } from "@/api/accounts";
import { useCategories, pickerOptions } from "@/api/categories";
import { matchPayee, usePayees } from "@/api/payees";
import { useCreateTransaction } from "@/api/transactions";
import { AmountError, TRAVEL_CURRENCIES } from "@/money";
import {
  ErrorNote,
  Field,
  Loading,
  Panel,
  PageTitle,
  buttonClass,
  inputClass,
} from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";
import { today } from "@/dates";

type Direction = "expense" | "income";

/**
 * The add-transaction form: the page K-11's acceptance criterion turns on.
 *
 * **Amount → payee → done** (K-15). Those two fields and the button are the
 * whole of the fast path; everything else has a default and sits behind one
 * summary line that says what those defaults currently are. The visual order is
 * the tab order: the amount is focused when the page opens, Tab reaches the
 * payee, Enter saves. A subsequent transaction is three interactions, and
 * `project.md` §5.2 says four is where the tool dies.
 *
 * Nothing is *hidden* by that — the summary line names the account, the date and
 * the category before it is opened. A default nobody can see is a default nobody
 * can correct.
 *
 * **The payee field pre-fills the category** (K-14). Typing a name the household
 * has used before fills in the category that name was last filed under — and
 * stops doing so the moment the user picks a category themselves, because a form
 * that overwrites a deliberate choice is worse than one that never helped.
 *
 * **Direction is a toggle, not a minus sign.** `model.md` §2.2 rule 1 makes
 * expenses negative and income positive, and that convention is worth keeping
 * all the way to the wire — but asking somebody to type a minus in front of
 * every purchase is how sign errors get made. The form takes a positive number
 * and a direction, and applies the sign itself.
 *
 * **No split UI.** This is K-10's single-split fast path: one amount, one date,
 * one category, and the server creates the split. The category is optional
 * because "uncategorised" is a real, queryable state in the model rather than a
 * magic row (`model.md` §3.4) — the quick-capture flow depends on being able to
 * record money now and classify it later.
 */
export function NewTransactionPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const accounts = useAccounts(householdId);
  const categories = useCategories(householdId);
  const create = useCreateTransaction(householdId);

  const preselected = params.get("account_id") ?? "";
  const [accountId, setAccountId] = useState(preselected);
  const [direction, setDirection] = useState<Direction>("expense");
  const [amount, setAmount] = useState("");
  const [bookedDate, setBookedDate] = useState(today());
  const [categoryId, setCategoryId] = useState("");
  const [payee, setPayee] = useState("");
  // Whether the category was chosen by hand. Once it was, the pre-fill stays out
  // of the way.
  const [categoryChosen, setCategoryChosen] = useState(false);
  const [note, setNote] = useState("");
  const [foreignAmount, setForeignAmount] = useState("");
  // Typed as string rather than inferred: the list is a literal tuple so the
  // first element has a literal type, and the select below sets any of them.
  const [foreignCurrency, setForeignCurrency] = useState<string>(TRAVEL_CURRENCIES[0]);
  const [amountError, setAmountError] = useState<string>();

  const payees = usePayees(householdId, payee);
  const suggestions = payees.data?.items ?? [];
  // The payee the typed name resolves to, by the same rule the server uses.
  const known = matchPayee(suggestions, payee);

  const list = accounts.data?.data ?? [];
  // The account decides the currency: a transaction is denominated in its
  // account's currency, and the schema makes disagreeing impossible
  // (`model.md` §5.6). So the form reads it rather than offering a choice.
  const selected = list.find((a) => a.id === (accountId || list[0]?.id));
  const currency = selected?.currency_code ?? "EUR";

  /**
   * The pre-fill itself (K-14).
   *
   * It runs as an effect rather than inside the change handler because the
   * suggestions arrive from the server a moment *after* the keystroke that asked
   * for them — doing it on keypress would only work when the answer happened to
   * be cached already, which is exactly the second time and never the first.
   *
   * It fires on an exact match rather than on the first suggestion, so it never
   * files a booking under a payee somebody was still typing past. And it leaves
   * a chosen category alone: helping is the point, overruling is not.
   */
  useEffect(() => {
    if (categoryChosen) return;
    setCategoryId(known?.default_category_id ?? "");
  }, [known?.id, known?.default_category_id, categoryChosen]);

  const violation = violationsOf(t, create.error);


  const options = pickerOptions(categories.data?.items ?? []);
  const prefilledCategory = options.find((o) => o.id === categoryId)?.label.trim();

  // The summary line: what the form will send for everything the fast path does
  // not ask about. It is written out rather than left implicit because a default
  // nobody can see is a default nobody can correct.
  const defaultsSummary = [
    selected?.name ?? t("transaction.noAccount"),
    bookedDate === today() ? t("transaction.today") : format.date(bookedDate),
    prefilledCategory ?? t("transaction.noCategory"),
  ].join(" · ");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAmountError(undefined);

    if (!selected) {
      setAmountError(t("transaction.chooseAnAccount"));
      return;
    }

    let minor: number;
    try {
      minor = format.parse(amount, currency);
    } catch (err) {
      setAmountError(err instanceof AmountError ? t(err.key, err.params) : String(err));
      return;
    }

    // The typed number is a magnitude; the toggle supplies the sign. Math.abs
    // means a user who typed the minus anyway gets what they meant rather than
    // an accidental double negative.
    const signed = direction === "expense" ? -Math.abs(minor) : Math.abs(minor);
    if (signed === 0) {
      setAmountError(t("transaction.amountNotZero"));
      return;
    }

    // What was actually spent abroad, if anything (K-18, model.md §6.3). It
    // carries the same sign as the booking — an expense in euros is an expense
    // in francs — and no rate goes with it: the server works that out from the
    // two amounts, which is the whole point of asking for them.
    let foreignMinor: number | undefined;
    if (foreignAmount.trim() !== "") {
      try {
        const parsed = format.parse(foreignAmount, foreignCurrency);
        foreignMinor = signed < 0 ? -Math.abs(parsed) : Math.abs(parsed);
      } catch (err) {
        setAmountError(err instanceof AmountError ? t(err.key, err.params) : String(err));
        return;
      }
      if (foreignMinor === 0) {
        setAmountError(t("transaction.foreignAmountNotZero"));
        return;
      }
    }

    create.mutate(
      {
        account_id: selected.id,
        booked_date: bookedDate,
        amount_minor: signed,
        ...(categoryId ? { category_id: categoryId } : {}),
        // Both, when the name is one the household already knows: the id is the
        // resolution the user saw, and the raw text is what they typed. The
        // server keeps the raw either way, so a wrong resolution can be redone
        // from the source (`model.md` §3.3).
        ...(payee ? { payee_raw: payee } : {}),
        ...(known ? { payee_id: known.id } : {}),
        ...(note ? { note } : {}),
        ...(foreignMinor !== undefined
          ? {
              original_amount_minor: foreignMinor,
              original_currency_code: foreignCurrency,
            }
          : {}),
      },
      { onSuccess: () => navigate(`/households/${householdId}/transactions`) },
    );
  }

  if (accounts.isLoading) return <Loading />;

  return (
    <div className="max-w-md">
      <PageTitle>{t("transactions.add")}</PageTitle>

      <Panel>
      <form onSubmit={onSubmit} noValidate>
        {/*
          Direction sits above the amount because it changes what the amount
          means, and before it in the DOM so that Tab from the amount goes
          *forward* to the payee. Shift+Tab reaches it, which is the right cost
          for the rarer answer: most entries are expenses.
        */}
        <fieldset>
          <legend className="block text-sm font-medium text-ink">{t("transaction.direction")}</legend>
          <div className="mt-1.5 flex gap-2">
            {(
              [
                ["expense", t("categories.expense")],
                ["income", t("categories.income")],
              ] as Array<[Direction, string]>
            ).map(([value, label]) => (
              <label
                key={value}
                className={`flex min-h-11 flex-1 cursor-pointer items-center justify-center rounded-control border px-3 text-center text-base transition-colors sm:text-sm ${
                  direction === value
                    ? "border-accent bg-accent-soft font-medium text-accent-text"
                    : "border-line text-muted hover:text-ink"
                }`}
              >
                <input
                  type="radio"
                  name="direction"
                  value={value}
                  checked={direction === value}
                  onChange={() => setDirection(value)}
                  className="sr-only"
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        <Field
          label={`${t("transactions.amount")} (${currency})`}
          htmlFor="tx-amount"
          hint={t("transaction.amountHint")}
          error={amountError ?? violation("amount_minor")}
        >
          <input
            id="tx-amount"
            name="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            enterKeyHint="next"
            placeholder={format.plain(1_250, currency)}
            autoFocus
            className={`${inputClass} num min-h-12 text-lg`}
          />
        </Field>

        <Field
          label={`${t("transactions.payee")} (${t("common.optional")})`}
          htmlFor="tx-payee"
          hint={
            known?.default_category_id && !categoryChosen
              ? t("transaction.categoryPrefilled", { category: prefilledCategory ?? "" })
              : t("transaction.payeeHint")
          }
        >
          <input
            id="tx-payee"
            name="payee_raw"
            list="tx-payee-suggestions"
            value={payee}
            onChange={(e) => setPayee(e.target.value)}
            placeholder="REWE"
            autoComplete="off"
            enterKeyHint="done"
            className={`${inputClass} min-h-12 text-base`}
          />
          <datalist id="tx-payee-suggestions">
            {suggestions.map((p) => (
              <option key={p.id} value={p.name} />
            ))}
          </datalist>
        </Field>

        <button
          type="submit"
          disabled={create.isPending}
          className={`${buttonClass} mt-6 min-h-12 w-full text-base sm:text-sm`}
        >
          {create.isPending ? t("common.saving") : t("transaction.save")}
        </button>

        {/*
          Everything with a sensible default, behind one line that states the
          defaults. `details` is deliberately native: it is one tap, it is
          keyboard-reachable, and its contents leave the tab order entirely while
          it is closed — which is what makes Tab from the amount land on the
          payee instead of walking three fields nobody wanted to change.
        */}
        <details className="mt-6 rounded-control border border-line px-4">
          {/*
            `display: flex` on a summary removes the disclosure triangle, so the
            affordance is written out instead. It has to be visible: this line is
            the only thing telling somebody the form made three decisions for
            them.
          */}
          <summary className="-mx-4 flex min-h-11 cursor-pointer items-center justify-between gap-3 px-4 text-sm text-muted">
            <span className="truncate">{defaultsSummary}</span>
            <span className="shrink-0 text-accent-text">
              {t("transaction.change")}
            </span>
          </summary>

          <div className="pb-4">
            <Field
              label={t("transactions.account")}
              htmlFor="tx-account"
              error={violation("account_id")}
            >
              <select
                id="tx-account"
                name="account_id"
                value={selected?.id ?? ""}
                onChange={(e) => setAccountId(e.target.value)}
                className={`${inputClass} min-h-11 text-base sm:text-sm`}
              >
                {list.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.currency_code})
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label={t("transactions.date")}
              htmlFor="tx-date"
              error={violation("booked_date")}
            >
              <input
                id="tx-date"
                name="booked_date"
                type="date"
                value={bookedDate}
                onChange={(e) => setBookedDate(e.target.value)}
                className={`${inputClass} min-h-11 text-base sm:text-sm`}
              />
            </Field>

            <Field
              label={`${t("transaction.category")} (${t("common.optional")})`}
              htmlFor="tx-category"
              hint={t("transaction.categoryHint")}
              error={violation("category_id")}
            >
              <select
                id="tx-category"
                name="category_id"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setCategoryChosen(true);
                }}
                className={`${inputClass} min-h-11 text-base sm:text-sm`}
              >
                <option value="">{t("transaction.withoutCategory")}</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            {/*
              model.md §6.3: the coffee in Zurich. Both numbers are in front of
              the person — the receipt says CHF 4.50 and the statement says
              €4.82 — and neither of them is a rate to ten decimal places, so
              this asks for the two it can and derives the third.
            */}
            <Field
              label={`${t("transaction.paidAbroad")} (${t("common.optional")})`}
              htmlFor="tx-original-amount"
              hint={t("transaction.paidAbroadHint")}
              error={violation("original_amount_minor") ?? violation("original_currency_code")}
            >
              <div className="mt-1 flex gap-2">
                <input
                  id="tx-original-amount"
                  name="original_amount"
                  value={foreignAmount}
                  onChange={(e) => setForeignAmount(e.target.value)}
                  inputMode="decimal"
                  placeholder={format.plain(450, foreignCurrency)}
                  className={`${inputClass} num mt-0 min-h-11 text-base sm:text-sm`}
                />
                <select
                  id="tx-original-currency"
                  name="original_currency_code"
                  value={foreignCurrency}
                  onChange={(e) => setForeignCurrency(e.target.value)}
                  aria-label={t("transaction.receiptCurrency")}
                  className={`${inputClass} mt-0 min-h-11 w-28 text-base sm:text-sm`}
                >
                  {TRAVEL_CURRENCIES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
            </Field>

            <Field label={`${t("transaction.note")} (${t("common.optional")})`} htmlFor="tx-note">
              <input
                id="tx-note"
                name="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className={`${inputClass} min-h-11 text-base sm:text-sm`}
              />
            </Field>
          </div>
        </details>
      </form>
      </Panel>

      <ErrorNote error={create.error} />
    </div>
  );
}
